import { BlockHistoryField } from 'types/contacts';
import { CreateEntityFieldChangeReasonDto } from 'types/matters';

/**
 * Maps form field names to BlockHistoryField enum values.
 */
const FIELD_NAME_TO_ENUM: Record<string, BlockHistoryField> = {
    // Matter fields (the only ones written from the FE; party blocking flows through dedicated endpoints)
    matterBlockingPolicyType: BlockHistoryField.MATTER_BLOCKING_POLICY_TYPE,
    isAmlBlockingExempt: BlockHistoryField.MATTER_AML_BLOCKING_EXEMPT,
};

/**
 * Compares two values for equality, handling objects with 'value' property (like SelectInput options).
 */
const areValuesEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return a == b;

    // Handle objects with 'value' property (SelectInput options like {label, value})
    if (typeof a === 'object' && typeof b === 'object' && 'value' in a && 'value' in b) {
        return (a as { value: unknown }).value === (b as { value: unknown }).value;
    }

    return a === b;
};

/**
 * Builds the entityFieldChangeReasons array from form data.
 * Collects all fields where:
 * 1. The field has change tracking enabled (is in trackedFields array)
 * 2. The value has changed from the initial value
 *
 * @param formData - Current form data
 * @param defaultValues - Initial/default form values
 * @param trackedFields - Array of field names that have change reason tracking enabled
 * @returns Array of CreateEntityFieldChangeReasonDto
 */
export const buildEntityFieldChangeReasons = <T extends object>(
    formData: T & { changeReasons?: Record<string, string> | null },
    defaultValues: Partial<T>,
    trackedFields: (keyof T)[]
): CreateEntityFieldChangeReasonDto[] => {
    const changeReasons = formData.changeReasons ?? {};

    return trackedFields.flatMap((fieldName) => {
        const currentValue = formData[fieldName];
        const initialValue = defaultValues[fieldName];
        if (areValuesEqual(currentValue, initialValue)) return [];
        return [
            {
                fieldName: FIELD_NAME_TO_ENUM[String(fieldName)] ?? String(fieldName),
                oldValue: formatValueForDto(initialValue),
                newValue: formatValueForDto(currentValue),
                changeReasonText: changeReasons[String(fieldName)] ?? '',
            },
        ];
    });
};

/**
 * Formats a value for the DTO. Converts various types to string representation.
 */
const formatValueForDto = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    // For objects (like select options), try to get a meaningful representation
    if (typeof value === 'object' && 'value' in value) {
        return String((value as { value: unknown }).value);
    }
    return JSON.stringify(value);
};
